import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ConversationPanel from '../enhancement/ConversationPanel';
import { startContext, contextTurn, saveContext } from '../../services/profileContextService';
import { SAGE, CREAM } from '../../theme';

/**
 * "Provide context" — a short conversation that recovers the story behind one
 * resume bullet, and writes it to `Accomplishment.context`.
 *
 * The modal shell of design §12's "one component, two shells": anchored to the
 * item you were already looking at, so you stay on the profile page and come
 * back where you were. ConversationPanel is the shared surface, as in the
 * interview.
 *
 * What gets saved is a synthesis of what you said — not the transcript. The
 * transcript is kept for provenance but never published; only the synthesis is,
 * and only after being condensed against a specific requirement at share time.
 *
 * Props: open, onClose, accomplishment {id, text, company, context}, onSaved
 */
const ProvideContextModal = ({ open, onClose, accomplishment, onSaved }) => {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [suggestSave] = useState(false);
  const started = useRef(null);

  const accId = accomplishment?.id;

  useEffect(() => {
    if (!open || !accId || started.current === accId) return;
    started.current = accId;
    setMessages([]);
    setError(null);
    (async () => {
      setBusy(true);
      try {
        const r = await startContext(accId);
        setConversationId(r.conversation_id);
        setMessages([{ id: `a-${Date.now()}`, role: 'assistant', content: r.message }]);
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy(false);
      }
    })();
  }, [open, accId]);

  useEffect(() => {
    if (!open) started.current = null;
  }, [open]);

  const send = useCallback(async (text) => {
    const mine = { id: `u-${Date.now()}`, role: 'user', content: text };
    const next = [...messages, mine];
    setMessages(next);
    setBusy(true);
    setError(null);
    try {
      const history = next.map(({ role, content }) => ({ role, content }));
      const r = await contextTurn(accId, text, history);
      setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: 'assistant', content: r.message }]);
    } catch (e) {
      setError(e.message);
      setMessages((prev) => prev.filter((m) => m.id !== mine.id));
    } finally {
      setBusy(false);
    }
  }, [messages, accId]);

  const persist = async () => {
    setSaving(true);
    setError(null);
    try {
      const history = messages.map(({ role, content }) => ({ role, content }));
      const r = await saveContext(accId, history, conversationId);
      onSaved?.(r);
      onClose?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Saving before the conversation has anything in it would write a paragraph
  // synthesised from nothing.
  const canSave = messages.some((m) => m.role === 'user') && !busy && !saving;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: '76vh', display: 'flex', flexDirection: 'column',
          bgcolor: CREAM[50], border: `2px solid ${SAGE[200]}`, borderRadius: 4,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, py: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: SAGE[500] }}>
            {accomplishment?.company ? `${accomplishment.company} · ` : ''}
            Provide context
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: SAGE[800], mt: 0.25 }}>
            {accomplishment?.text}
          </Typography>
          {accomplishment?.context && (
            <Typography sx={{ fontSize: 11.5, color: SAGE[400], mt: 0.5 }}>
              This already has context — continuing will replace it.
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1, fontSize: 13 }}>{error}</Alert>
      )}

      <DialogContent dividers sx={{ p: 0, flex: 1, minHeight: 0 }}>
        {open && (
          <ConversationPanel
            messages={messages}
            onSendMessage={send}
            isProcessing={busy || saving}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 11.5, color: SAGE[400] }}>
          Saved to your profile — not published anywhere on its own.
        </Typography>
        <Button
          onClick={persist}
          disabled={!canSave}
          sx={{
            textTransform: 'none', fontFamily: "'Outfit', sans-serif",
            bgcolor: SAGE[500], color: '#fff', px: 2.5,
            '&:hover': { bgcolor: SAGE[600] },
            '&.Mui-disabled': { bgcolor: SAGE[100], color: SAGE[300] },
          }}
        >
          {saving ? 'Saving…' : 'Save context'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProvideContextModal;
